/**
 * A party list's money before Build (L33 of docs/plans/ledger.md, step 12.1):
 * the lists a money rule names as its party, and the setup's own totals over
 * ruled records that would count that money a second way.
 *
 * Pure: the same draft back when nothing changes.
 */
import { accountTypeOps, homeMoneyOps, ownerMoneyInOps } from './account-rows';
import { applyStudioOps } from './apply';
import type { StudioDraft } from './draft';
import { accountsListOf, postingOf } from './money-rules';

/**
 * The lists money names as its party (L20): the list each ruled record's
 * `party` role picks from, on its own rule or on any of its choices'.
 */
export const partyListsOf = (draft: Pick<StudioDraft, 'records'>): Set<string> => {
  const lists = new Set<string>();
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null) continue;
    const ways = [rule.roles ?? {}, ...Object.values(rule.byChoice?.options ?? {}).map((option) => option.roles ?? {})];
    for (const roles of ways) {
      const party = roles.party;
      if (party === undefined || !('fieldKey' in party)) continue;
      const target = record.fields.find((field) => field.key === party.fieldKey)?.targetRecordKey;
      if (target !== undefined && draft.records.some((one) => one.key === target && one.kind === 'directory')) lists.add(target);
    }
  }
  return lists;
};

/**
 * A party list's money from one source (L33 of docs/plans/ledger.md, step
 * 12.1). Build gives each party list Billed, Received and Pending from the
 * ledger (`addLedgerRollups`); a total the setup drew on the same list over a
 * record that has a money rule (W11's Billed, Received and Pending over sales
 * and payments) would count the same money a second way, so it goes before
 * Build, with what reads it (a Pending worked out from the two, a view that
 * filters on it). A total over a record with no rule stays, and so does one
 * the person named for itself (30 Oct, a contractor's Total kharcho and Total
 * aavela hapta on Sites, and the Kamani worked out from them): only a total
 * named as one of the three ledger figures says what the ledger says.
 */
/** The words of the three figures the ledger puts on a party list (Billed, Received, Pending), as a setup names its own totals of them. */
const LEDGER_FIGURE = /^(total\s+)?(amount\s+)?(billed|bills?|charged|sales?|sold|received|receipts?|collected|paid|payments?|pending|balance|due|dues|owed?|owing|outstanding|baaki|baki|udhaa?r)(\s+(amount|so far|total))?$/i;

export const ledgerOnlyRollups = (draft: StudioDraft): StudioDraft => {
  if (accountsListOf(draft) === null) return draft;
  const ruled = new Set(draft.records.filter((record) => postingOf(record) !== null).map((record) => record.key));
  let running = draft;
  for (const list of partyListsOf(draft)) {
    const held = running.records.find((one) => one.key === list);
    for (const field of held?.fields ?? []) {
      if (field.type !== 'rollup' || field.rollup === undefined || !ruled.has(field.rollup.overRecordKey) || !LEDGER_FIGURE.test(field.label.trim())) continue;
      if (!running.records.find((one) => one.key === list)?.fields.some((one) => one.key === field.key)) continue;
      const removed = applyStudioOps(running, [{ op: 'remove_field', record: list, field: field.key, cascade: 'with_dependents' }]);
      if (removed.ok) running = removed.draft;
    }
  }
  return running;
};

/** A draft as Build composes it: its accounts typed, home money and the owner's money put in out of profit, and each party list's money from the ledger alone. */
export const moneyBeforeBuild = (draft: StudioDraft): StudioDraft => ledgerOnlyRollups(accountsMended(draft));

/**
 * The Accounts list as Build makes it: each row typed where its type is
 * clear, and the owner's home money folded into For home (`account-rows.ts`).
 * A row whose type is still not clear is left for the validator to say.
 */
const accountsMended = (draft: StudioDraft): StudioDraft => {
  let running = draft;
  for (const ops of [accountTypeOps, homeMoneyOps, ownerMoneyInOps]) {
    const made = ops(running);
    if (made.length === 0) continue;
    const applied = applyStudioOps(running, made);
    if (applied.ok) running = applied.draft;
  }
  return running;
};
